import React, { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { getMunicipalities, type Municipality } from '../lib/api/municipalities';
import { logger } from '../lib/logger';

interface MunicipalitiesContextType {
  municipalities: Municipality[];
  municipalityNames: string[];
  loading: boolean;
  error: Error | null;
  getMunicipalityName: (id: string) => string;
}

const MunicipalitiesContext = createContext<MunicipalitiesContextType | undefined>(undefined);

export const useMunicipalities = () => {
  const context = useContext(MunicipalitiesContext);
  if (!context) {
    throw new Error('useMunicipalities must be used within MunicipalitiesProvider');
  }
  return context;
};

interface MunicipalitiesProviderProps {
  children: ReactNode;
}

export const MunicipalitiesProvider: React.FC<MunicipalitiesProviderProps> = ({ children }) => {
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchMunicipalities() {
      try {
        logger.log('[MunicipalitiesContext] Fetching municipalities');
        setLoading(true);
        const data = await getMunicipalities();

        if (isMounted) {
          setMunicipalities(data);
          setError(null);
          logger.log('[MunicipalitiesContext] Municipalities loaded:', data.length);
        }
      } catch (err) {
        logger.error('[MunicipalitiesContext] Error fetching municipalities', err);
        if (isMounted) {
          setError(err as Error);
          setMunicipalities([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchMunicipalities();

    return () => {
      isMounted = false;
    };
  }, []); // Only fetch once on mount - municipalities are shared across all organizations

  // Memoized array of municipality names for dropdown filters
  const municipalityNames = useMemo(() =>
    municipalities.map(m => m.name),
    [municipalities]
  );

  // Memoized lookup map for ID -> name conversion
  const municipalityLookup = useMemo(() =>
    new Map(municipalities.map(m => [m.id, m.name])),
    [municipalities]
  );

  // Helper function to get municipality name from ID
  const getMunicipalityName = (id: string): string => {
    return municipalityLookup.get(id) || 'Sin municipio';
  };

  const value = {
    municipalities,
    municipalityNames,
    loading,
    error,
    getMunicipalityName,
  };

  return (
    <MunicipalitiesContext.Provider value={value}>
      {children}
    </MunicipalitiesContext.Provider>
  );
};
